import React, { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import paymentIntegrationService, { HANDLE_MAX } from "@/services/paymentIntegrationService";

// No celular o diálogo vira "bottom sheet" (preso embaixo, entra de baixo)
const SHEET_CLASSES =
  "max-sm:inset-x-0 max-sm:bottom-0 max-sm:top-auto max-sm:left-0 max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-b-none max-sm:rounded-t-3xl max-sm:border-x-0 max-sm:border-b-0 max-sm:pb-[calc(1.5rem+env(safe-area-inset-bottom,0px))] max-sm:data-[state=open]:slide-in-from-left-0 max-sm:data-[state=open]:slide-in-from-bottom-full max-sm:data-[state=closed]:slide-out-to-left-0 max-sm:data-[state=closed]:slide-out-to-bottom-full max-sm:data-[state=open]:zoom-in-100 max-sm:data-[state=closed]:zoom-out-100";

const inputClass =
  "h-11 w-full rounded-xl border bg-card pl-8 pr-3.5 font-mono text-base text-foreground transition-colors placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-[3px] focus:ring-brand-pistachio/25 disabled:opacity-60 md:text-sm";

/**
 * Configuração da InfinitePay (Checkout Integrado): só a InfiniteTag (handle)
 * da conta, sem segredo. `integration` é o registro do GET (com `version`).
 * `activeOther`: integração de outro provedor ativa hoje (ativar esta a desativa).
 * onSave recebe `{ handle, enabled, version }` para o PUT /payment-integrations/infinitepay.
 */
function InfinitePayFormModal({ open, onOpenChange, integration, activeOther, onSave, loading }) {
  const [handle, setHandle] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [errors, setErrors] = useState({});
  const handleRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setErrors({});
    setHandle(integration?.handle ?? "");
    // Primeira configuração já sugere ativar se nenhuma outra estiver ativa
    setEnabled(integration?.configured ? Boolean(integration.enabled) : !activeOther);
  }, [integration, activeOther, open]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (loading) return;
    const { errors: nextErrors, isValid } = paymentIntegrationService.validateInfinitePay({ handle });
    setErrors(nextErrors);
    if (!isValid) {
      handleRef.current?.focus();
      return;
    }
    onSave({
      handle: paymentIntegrationService.normalizeHandle(handle),
      enabled,
      version: integration?.version ?? null,
    });
  };

  const willReplace = enabled && activeOther && !integration?.enabled;

  return (
    <Dialog open={open} onOpenChange={(next) => !loading && onOpenChange(next)}>
      <DialogContent
        className={cn("max-h-[90vh] max-w-md gap-0 overflow-y-auto rounded-2xl bg-background p-6", SHEET_CLASSES)}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          handleRef.current?.focus();
        }}
      >
        <div className="mx-auto -mt-2 mb-4 h-1.5 w-12 rounded-full bg-border sm:hidden" aria-hidden="true" />
        <DialogHeader className="space-y-0.5 pb-4 pr-8 text-left">
          <DialogTitle className="font-display text-lg font-bold tracking-tight text-foreground">
            Configurar InfinitePay
          </DialogTitle>
          <DialogDescription className="text-xs">
            Receba pagamentos online (Pix e cartão) pelo Checkout Integrado da InfinitePay.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          <div>
            <Label htmlFor="infinitepay-handle" className="mb-1 block text-xs font-semibold">
              InfiniteTag <span className="text-destructive">*</span>
            </Label>
            <div className="relative flex items-center">
              <span className="pointer-events-none absolute left-3.5 font-mono text-sm text-muted-foreground" aria-hidden="true">
                $
              </span>
              <input
                id="infinitepay-handle"
                ref={handleRef}
                value={handle}
                maxLength={HANDLE_MAX + 1}
                onChange={(e) => {
                  setHandle(e.target.value.replace(/^\$+/, ""));
                  if (errors.handle) setErrors({});
                }}
                placeholder="minha-loja"
                autoComplete="off"
                autoCapitalize="none"
                spellCheck={false}
                disabled={loading}
                aria-invalid={Boolean(errors.handle) || undefined}
                aria-describedby="infinitepay-handle-hint"
                className={cn(inputClass, errors.handle ? "border-destructive" : "border-border")}
              />
            </div>
            {errors.handle ? (
              <p id="infinitepay-handle-hint" className="mt-1 text-xs font-medium text-destructive" role="alert">
                {errors.handle}
              </p>
            ) : (
              <p id="infinitepay-handle-hint" className="mt-1 text-[11px] text-muted-foreground">
                Seu nome de usuário no App InfinitePay, sem o "$".
              </p>
            )}
          </div>

          <label
            htmlFor="infinitepay-enabled"
            className="flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-card/80 p-3"
          >
            <span className="pr-1">
              <span className="block text-xs font-semibold text-foreground">Usar nos pagamentos online</span>
              <span className="mt-0.5 block text-[11px] leading-tight text-muted-foreground">
                Só uma integração fica ativa por empresa.
              </span>
            </span>
            <Switch
              id="infinitepay-enabled"
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={loading}
            />
          </label>

          {willReplace && (
            <p className="rounded-xl border border-terra/30 bg-terra-soft p-3 text-[12px] leading-snug text-terra" role="status">
              Ao salvar, <span className="font-semibold">{activeOther.providerName}</span> será desativada e
              os novos pagamentos online passarão a usar a InfinitePay.
            </p>
          )}

          <div className="flex items-center justify-end gap-2.5 pt-2">
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={loading}
              className="h-10 rounded-full border border-border bg-surface-container px-5 text-[13px] font-semibold text-foreground transition-colors hover:bg-surface-high disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex h-10 items-center justify-center gap-1.5 rounded-full bg-primary px-6 text-[13px] font-semibold text-primary-foreground shadow-sm transition-all hover:bg-brand-dark active:scale-95 disabled:opacity-70"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {loading ? "Salvando..." : "Salvar"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default InfinitePayFormModal;
